import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, take } from 'rxjs';
import { selectAuth } from 'src/app/store/auth/auth.reducers';
import { selectIsFullyAuthenticated } from 'src/app/store/app-ready.selectors';
import { UserSettingsService } from './user-settings.service';

const CHANNEL_NAME = 'user-settings';

/**
 * Shared plumbing for the root services that cache a `/v2/userSettings` preference: the
 * cross-tab `BroadcastChannel`, the once-per-login load, and the optimistic write. Each concrete
 * store owns its own BehaviorSubject(s) and keys; this base only holds the parts that were
 * previously copied verbatim across DateFormatService and NumberFormatService (and, for the
 * auth-transition load, ReleaseNotesStateService). A fix to the wiring — the swallowed-error
 * handler, payload coercion, the CD scheduling below — now lands here once instead of drifting.
 *
 * `@Injectable()` (no `providedIn`) is required because this base uses Angular features
 * (DI'd constructor params + the OnDestroy hook); the concrete stores carry the real
 * `providedIn: 'root'` registration.
 */
@Injectable()
export abstract class UserSettingStore implements OnDestroy {
  private channel?: BroadcastChannel;
  private storeInitialized = false;
  // Id of the user the current load reflects, so the load runs once per IDENTITY (not once per /app
  // entry): selectIsFullyAuthenticated toggles false→true whenever the URL leaves and re-enters /app
  // within one login, which without this would re-fetch /v2/userSettings on every /app re-entry. Keyed
  // on the userId (not a plain boolean) so a cross-tab identity switch A→B — where isAuthenticated never
  // goes false — is detected and reloads B's prefs instead of leaving A's applied. Reset on logout.
  private loadedForUserId?: number;
  /**
   * Whether an identity has been seen at all since the last teardown — the gate on the logout
   * cleanup below, and deliberately NOT `loadedForUserId`.
   *
   * The two are different facts and conflating them silently disarms the cleanup: `rearmLoad()`
   * clears `loadedForUserId` after a FAILED load so the next /app entry retries, so a load that
   * errored leaves the store looking exactly like a tab that has never logged in — and the
   * `!userId` branch then skips `onLoggedOut()` entirely. For a store that only caches a value
   * that is about to be overwritten that is invisible; for one whose applied state must not
   * outlive its owner (AttributePresetStore's `onIdentityGone`, CvdService's override of it) it
   * means a failed settings GET is all it takes for the outgoing user's palette and mirror to
   * survive their own logout.
   *
   * It is set for an identity that is authenticated but not yet ON /app, ahead of the readiness
   * return, because the cleanup it gates is about the identity existing, not about the load
   * having run. It must NOT be set on the `!userId` branch itself, which is what makes that branch
   * able to tell a LOGOUT from a tab that came up with nobody signed in.
   *
   * WHAT IT DOES NOT GATE, and the correction worth reading before re-tightening it: the `!userId`
   * branch used to skip its cleanup ENTIRELY when this was false, on the stated ground that "the
   * first emission at boot is (false, 0)" and clearing there would reset over the boot script's
   * stamp on every boot. That premise is wrong. `AuthRefreshService.bootstrap()` returns a Promise
   * and is an awaited APP_INITIALIZER, while every init() here is called from AppComponent — so a
   * tab whose refresh cookie is still good has dispatched APPLY_SESSION with its real userId before
   * this subscription exists, and its first emission carries that identity. The reload the flash
   * argument is about never reaches the `!userId` branch at all; the boot that does has no session
   * behind the stamp. `onNoIdentityAtStartup()` is the branch that follows from saying so, and
   * app.module.spec.ts is the gate on the ordering it rests on.
   */
  private sawIdentity = false;

  /** Whether this store participates in cross-tab sync. When false, no BroadcastChannel is
   *  opened and no CD-scheduling `zone.run(onBroadcast)` fires for this instance. Override to
   *  false in a subclass that neither posts (never calls persist()) nor consumes (onBroadcast
   *  is a no-op) — e.g. ReleaseNotesStateService, which reuses this base ONLY for the
   *  per-login load and would otherwise wake on every unrelated date/number preset change in
   *  another tab. Read in the constructor, so it must not touch subclass instance fields
   *  (a literal getter is safe — the prototype method exists during super()). */
  protected get syncsAcrossTabs(): boolean {
    return true;
  }

  /** Whether a handled cross-tab broadcast should invalidate the shared `/v2/userSettings` blob
   *  cache. True for the display-preference stores, whose value IS a key in that blob — a sibling's
   *  write made the cached blob stale. Override to false in a store whose value lives on its own
   *  endpoint/column rather than in the blob (AvatarService): busting the settings cache there is a
   *  wasted re-fetch of a blob the avatar change never touched. Read on the message path, so it may
   *  reference subclass fields. */
  protected get broadcastInvalidatesSettingsCache(): boolean {
    return true;
  }

  constructor(
    protected userSettings: UserSettingsService,
    protected store: Store,
    private zone: NgZone,
  ) {
    if (this.syncsAcrossTabs && typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      // BroadcastChannel.onmessage is NOT patched by zone.js, so a subject.next() fired straight
      // from here updates the value WITHOUT scheduling change detection — the impure display
      // pipes (appNumber/appCurrency/appDate) in THIS tab would keep the old preset until some
      // unrelated interaction happens to trigger CD, while input fields (which repaint the DOM
      // directly from their own subscription) update immediately. Re-enter the Angular zone so a
      // cross-tab change repaints every consumer at once.
      // Re-enter the zone ONLY for a key this store handles. Every concrete store opens its own
      // channel on the shared CHANNEL_NAME, and distinct BroadcastChannel objects receive each
      // other's posts — so a date-preset write is also delivered to NumberFormatService (and vice
      // versa). Without the handlesKey() gate each foreign delivery would still run
      // zone.run(onBroadcast) — a no-op emit() — yet the zone re-entry schedules a wasted app-wide
      // CD pass re-invoking every impure appNumber/appCurrency/appDate pipe. Filter before the zone.
      this.channel.onmessage = (e: MessageEvent) => {
        if (this.handlesKey((e.data as { key?: string } | undefined)?.key)) {
          // A sibling tab persisted a preference the SERVER now holds a new value for, so the
          // cached /v2/userSettings blob (shareReplay'd for the whole session in UserSettingsService)
          // is stale. The pipes/inputs update from onBroadcast below, but any consumer that re-reads
          // the blob directly — PreferencesComponent.ngOnInit — would otherwise show the OLD value
          // for this key while every number/date on screen renders the new one, and writing from
          // that stale dropdown would clobber the sibling's change. Drop the cache so the next
          // getAll() re-fetches. Gated by handlesKey so only a real change to a display pref (not a
          // foreign no-op delivery on the shared channel) busts it. A store whose value does NOT
          // live in the /v2/userSettings blob (AvatarService) opts out via
          // broadcastInvalidatesSettingsCache so it doesn't drop a cache it never populated. */
          if (this.broadcastInvalidatesSettingsCache) {
            this.userSettings.invalidate();
          }
          this.zone.run(() => this.onBroadcast(e.data));
        }
      };
    }
  }

  ngOnDestroy(): void {
    this.channel?.close();
  }

  /** Wire the per-login load. Safe to call more than once. Invoked from AppComponent. */
  init(): void {
    if (this.storeInitialized) return;
    this.storeInitialized = true;
    // Load only once the user is actually on an /app route (selectIsFullyAuthenticated), NOT on the raw
    // isAuthenticated flag: that flips true the instant a token is applied — including the restricted
    // forced-2FA-enrollment token and the brief window on /auth/login before navigation — so loading
    // then hits /v2/userSettings with a session that can't reach it (401 → interceptor tears the
    // session down → bounce to /login). The /app route is set only after navigation + guards.
    //
    // Keying the load on the userId (via loadedForUserId) keeps it to ONE load per identity:
    // selectIsFullyAuthenticated goes false again every time the user leaves /app and true again on
    // return, so keying on readiness alone would re-fetch on each /app re-entry. Tracking WHICH user the
    // cache reflects also catches a cross-tab identity switch (user B signs in on another tab while this
    // tab is A): the shared refresh cookie means isAuthenticated never drops to false, so a logout-only
    // re-arm would leave A's prefs applied to B — here the userId change triggers a reload.
    combineLatest([
      this.store.select(selectIsFullyAuthenticated).pipe(distinctUntilChanged()),
      this.store.select(selectAuth).pipe(map(auth => auth.userId), distinctUntilChanged()),
    ]).subscribe(([ready, userId]) => {
      if (!userId) {
        // Logged out: drop the cached settings blob (both the getAll and the per-identity shared cache)
        // so the next login — possibly a different user — re-fetches instead of serving the previous
        // user's settings, and re-arm. Don't touch the applied prefs here — the next load overwrites them.
        if (this.sawIdentity) {
          this.userSettings.invalidate();
          this.userSettings.invalidateShared();
          this.onLoggedOut();
        } else {
          // Nobody has signed in in this tab and nobody is arriving — see onNoIdentityAtStartup.
          // No cache to drop here: nothing has ever fetched.
          this.onNoIdentityAtStartup();
        }
        this.sawIdentity = false;
        this.loadedForUserId = undefined;
        return;
      }
      // An identity exists. Record that BEFORE the readiness return — see sawIdentity for why the
      // logout cleanup is gated on this rather than on whether a load ever succeeded.
      this.sawIdentity = true;
      // ...and tell the store WHICH identity, also before the readiness return. See
      // onIdentityPresent: a store that learns this from load() alone learns nothing at all for an
      // identity that never reaches /app.
      this.onIdentityPresent(userId);
      // Authenticated but not yet on an /app route (on /auth, or holding the restricted enrollment
      // token): wait — loading now would hit /v2/userSettings with a session that can't reach it.
      if (!ready || this.loadedForUserId === userId) {
        return;
      }
      // A new identity on an /app route: a fresh login, or a cross-tab switch to a different user. Don't
      // invalidateShared() here — load() passes the userId as the cache key, so getAllShared rebuilds
      // once for the new identity and the sibling consumers reuse that same fetch. Invalidating first
      // would drop a cache a sibling may have just built, causing a second identical GET.
      this.loadedForUserId = userId;
      this.load(userId);
    });
  }

  /** Re-arm the per-identity load guard after a FAILED load so the next /app re-entry retries — but only
   *  while it still points at THIS load (a newer login for a different user may have already advanced it,
   *  and we must not clobber that newer load's guard). Called from a concrete load()'s error handler.
   *  It deliberately does NOT touch `sawIdentity` — re-arming a retry says nothing about whether an
   *  identity is present, and the two were one field until a failed load was found to disarm the
   *  logout cleanup. See sawIdentity. */
  protected rearmLoad(userId: number): void {
    if (this.loadedForUserId === userId) {
      this.loadedForUserId = undefined;
    }
  }

  /**
   * Called on EVERY emission that carries a userId — a fresh login, a cross-tab switch, and each
   * later /app re-entry within one login — BEFORE the readiness return, and so before `load()`.
   * A no-op here; a store that tracks which identity its APPLIED state belongs to overrides it.
   *
   * IT IS HERE AND NOT IN `load()` BECAUSE READINESS IS NOT IDENTITY. `selectIsFullyAuthenticated`
   * is `isAuthenticated && onApp && !enrollmentRequired`, so an incoming user who is
   * forced-2FA-enrollment-scoped never satisfies it: `AuthSyncService.leaveAfterSwitch(true)`
   * sends the tab straight to the enrollment wizard, `load()` is never called, and a store that
   * did its identity bookkeeping there would still believe the OUTGOING user is in place — with
   * their state applied — for as long as the incoming one stays in the wizard, and for good if
   * they abandon it. That is a supported path (`auth-sync.service.ts` takes it by name), not an
   * exotic race, and it is precisely the identity-scoped disclosure `AttributePresetStore`'s
   * `onIdentityGone` exists to close.
   *
   * Called on every emission, so an implementation must be IDEMPOTENT and act only on a CHANGE.
   */
  protected onIdentityPresent(_userId: number): void {
    // no-op by default
  }

  /** Called once when an identity goes away (logout / session teardown). The base deliberately does
   *  NOT reset applied preferences there — the next load overwrites them, and a number format
   *  lingering for a few hundred milliseconds is invisible. That reasoning does NOT hold for
   *  personal, visible state such as an avatar: the previous user's face would sit on screen until
   *  the next user's load returned. Such a store overrides this and clears its own subject. */
  protected onLoggedOut(): void {
    // no-op by default
  }

  /**
   * Called on a `!userId` emission when NO identity has been seen in this tab — the tab came up
   * with nobody signed in, and nobody is on the way.
   *
   * A SEPARATE HOOK RATHER THAN AN UNGATED onLoggedOut(), because the two are different events and
   * a subclass already tells them apart by side effect: AvatarService's onLoggedOut() drops a face
   * that was on screen, which at startup there never was. The cache invalidation above is gated for
   * the same reason — nothing has fetched yet, so there is nothing to drop.
   *
   * A store that merely CACHES a value has nothing to do here, hence the no-op default: the next
   * load overwrites it. A store whose APPLIED state must not outlive its owner does, and the reason
   * is not symmetry — the index.html boot script may have painted this page from a mirror written by
   * a previous occupant of the tab, and with no session behind it there is no identity to match that
   * mirror against and nobody it could legitimately belong to. See AttributePresetStore's override
   * for why reaching this hook at all means exactly that.
   */
  protected onNoIdentityAtStartup(): void {
    // no-op by default
  }

  /** Optimistic persist: the default value means "no stored override" -> DELETE, otherwise
   *  upsert. This tab already updated its own subject optimistically (see setPref); broadcast to
   *  OTHER tabs only once the write CONFIRMS, so a failed write doesn't propagate a value the
   *  server never stored (which would leave sibling tabs on a preset that vanishes on their next
   *  reload). Errors are swallowed — the local value stays as the user picked and self-heals on
   *  the next successful write. */
  protected persist(key: string, value: unknown, isDefault: boolean): void {
    const op = isDefault ? this.userSettings.reset(key) : this.userSettings.set(key, value);
    op.pipe(take(1)).subscribe({
      next: () => this.broadcast(key, value),
      error: () => {},
    });
  }

  /** Post a change on the shared cross-tab channel. Extracted from {@link persist} so a subclass
   *  that writes through its OWN endpoint (rather than /v2/userSettings) can still notify sibling
   *  tabs — `channel` itself is private, and duplicating a second BroadcastChannel per store would
   *  mean two channels racing on the same key. */
  protected broadcast(key: string, value: unknown): void {
    this.channel?.postMessage({ key, value });
  }

  /** Guarded optimistic setter shared by every concrete store's set<Pref>() method: skip the
   *  emission AND the write when the value already matches, so re-picking the active option
   *  fires no no-op app-wide re-render and no redundant HTTP write. `defaultValue` selects
   *  DELETE vs upsert via persist(). Replaces four near-identical guard+next+persist bodies. */
  protected setPref<T extends string>(
    subject: BehaviorSubject<T>, key: string, value: T, defaultValue: T,
  ): void {
    if (value === subject.value) return;
    subject.next(value);
    this.persist(key, value, value === defaultValue);
  }

  /** Emit only on a real change. A load or cross-tab broadcast that reproduces the current
   *  value would otherwise schedule a no-op app-wide change-detection pass (re-running every
   *  impure appDate/appNumber/appCurrency pipe) for a value that didn't change. Shared by every
   *  concrete store's load()/onBroadcast() so the guard can't drift between siblings. */
  protected emit<T>(subject: BehaviorSubject<T>, value: T): void {
    if (value !== subject.value) subject.next(value);
  }

  /** Whether a cross-tab message for `key` belongs to this store — checked BEFORE re-entering the
   *  Angular zone so a sibling store's write (delivered on the shared channel) costs no wasted CD
   *  pass here. Default `true` (handle everything) keeps a store that doesn't override it correct,
   *  just un-optimised; each concrete store overrides it to name only its own key(s). Must not touch
   *  subclass instance fields — it can run before the subclass constructor body via onmessage. */
  protected handlesKey(_key?: string): boolean {
    return true;
  }

  /** Load this store's key(s) from the settings blob (called once per authenticated identity on /app).
   *  `userId` is both the once-per-identity guard value and the per-identity cache key passed to
   *  UserSettingsService.getAllShared*(userId). */
  protected abstract load(userId: number): void;

  /** Apply a cross-tab change for this store's key(s). Already re-entered into the Angular zone. */
  protected abstract onBroadcast(data: { key?: string; value?: unknown }): void;
}
